<template>
  <v-card class="rc-card" :class="{ 'rc-card--ai-running': aiEditRunning }">
    <div class="rating-actions">
      <v-card-title>
        <a class="mr-3" @click="() => openOrder(rating.orderId)">{{
          rating.orderName
        }}</a>
        <v-icon :color="rating.satisfied ? 'success' : 'error'">{{
          rating.satisfied ? "mdi-thumb-up" : "mdi-thumb-down"
        }}</v-icon>
        <span v-if="rating.isLive" class="rc-live-badge">LIVE</span>
        <v-spacer />
        <OrderNoteTooltip :task="rating.task" />
        <SupportDialog :order-id="rating.orderId" />
      </v-card-title>
      <v-card-subtitle> by {{ artistName }}, {{ timeAgo }} </v-card-subtitle>
    </div>
    <v-card-text class="card-content">
      <div v-if="resolved" class="card-overlay">
        <v-icon size="130" color="white">mdi-check-circle</v-icon>
      </div>

      <!-- Zugeklappt: Mockup füllt, Kommentar als Sprechblase darunter -->
      <template v-if="!expandComment">
        <v-img
          contain
          class="rc-mockup"
          :src="rating.mockupImage || rating.resultThumbnailUrl"
        />
        <div v-if="latestCommentText" class="rc-comment" @click="expandComment = true">
          <v-icon small class="rc-comment__icon">mdi-format-quote-open</v-icon>
          <span class="rc-comment__text">{{ latestCommentText }}</span>
          <v-icon small class="rc-comment__chevron">mdi-chevron-down</v-icon>
        </div>
      </template>

      <!-- Aufgeklappt: Bild bleibt (kompakt), Kommentar in einer Box wie im
           eingeklappten Zustand — die ganze Box klappt per Klick wieder zu -->
      <div v-else class="rc-thread" @click="expandComment = false">
        <v-icon small class="rc-thread__collapse">mdi-chevron-up</v-icon>
        <v-img
          contain
          height="120"
          class="rc-thread__img"
          :src="rating.mockupImage || rating.resultThumbnailUrl"
        />
        <FeedbackThread :feedback="task.feedback" class="rc-thread__body" />
      </div>
    </v-card-text>

    <v-card-actions class="rating-actions">
      <template v-if="!rating.satisfied">
        <!-- Auto-KI-Fix-Status links in der Aktionszeile (feste Kartenhöhe) -->
        <template v-if="autoEditChip">
          <div
            class="rc-autoedit__chip"
            :class="`rc-autoedit__chip--${autoEditChip.tone}`"
            @click="autoEditChip.opensEditor && (showLiveEditor = true)"
            :style="autoEditChip.opensEditor ? 'cursor:pointer' : ''"
          >
            <v-progress-circular
              v-if="autoEditChip.spinner"
              indeterminate
              size="12"
              width="2"
              class="mr-1"
            />
            <v-icon v-else-if="autoEditChip.icon" x-small class="mr-1">{{ autoEditChip.icon }}</v-icon>
            {{ autoEditChip.label }}
          </div>
          <v-tooltip v-if="autoEditChip.canRunNow" open-delay="200" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="runAutoEditNow">
                <v-icon small>mdi-play-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ autoEdit.status === 'limit' ? 'Trotz Limit jetzt ausführen' : 'Jetzt ausführen' }}</span>
          </v-tooltip>
          <v-tooltip v-if="autoEditChip.canCancel" open-delay="200" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="cancelAutoEdit">
                <v-icon small>mdi-close-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>Auto-Fix abbrechen</span>
          </v-tooltip>
        </template>
        <v-spacer />

        <!-- Feedback ohne Änderung auflösen: Bestellung geht in Produktion -->
        <v-tooltip open-delay="200" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small icon v-bind="attrs" v-on="on" @click="showResolveDialog = true">
              <v-icon small>mdi-thumb-up</v-icon>
            </v-btn>
          </template>
          <span>Feedback auflösen — Bestellung geht in Produktion</span>
        </v-tooltip>

        <!-- Direkt-Aktionen: Kunde kontaktieren + Mail-Suche -->
        <v-tooltip open-delay="200" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small icon v-bind="attrs" v-on="on" @click="openMailDialog">
              <v-icon small>mdi-email</v-icon>
            </v-btn>
          </template>
          <span>Kunde kontaktieren</span>
        </v-tooltip>
        <v-tooltip open-delay="200" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small icon class="mr-1" v-bind="attrs" v-on="on" @click="searchOrderInMails">
              <v-icon small>mdi-email-search-outline</v-icon>
            </v-btn>
          </template>
          <span>In Mails suchen</span>
        </v-tooltip>

        <!-- Sekundäraktionen -->
        <v-menu bottom offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mr-2" small icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item :disabled="pushedBack" @click="openPushBackDialog">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-backspace</v-icon></v-list-item-icon>
              <v-list-item-title>Task zurückschieben</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openReassignDialog">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-account-reactivate</v-icon></v-list-item-icon>
              <v-list-item-title>Neu zuweisen (To Pay)</v-list-item-title>
            </v-list-item>
            <!-- Änderungslink: Kunde ändert sein Bild selbst; Task wird sofort geparkt, Bewertung verschwindet aus der Liste -->
            <v-list-item v-if="task.customizationData && task.customizationData.id" :disabled="editLinkBusy" @click="sendEditLink">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-image-edit-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Änderungslink per Mail senden</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="task.customizationData" :disabled="uploaded" @click="openReUploadDialog">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-upload</v-icon></v-list-item-icon>
              <v-list-item-title>Neues Ergebnis hochladen</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="rating.shared || isOwnFeedback" @click="showShareDialog = true">
              <v-list-item-icon class="mr-3"><v-icon small>mdi-share-variant</v-icon></v-list-item-icon>
              <v-list-item-title>{{ ratingBtnText }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Primäraktion: LiveEditor (Live-Task) bzw. Re-Upload (Klassisch) -->
        <v-btn
          v-if="task.customizationData"
          small
          depressed
          dark
          color="#7b5cd6"
          @click="() => (showLiveEditor = true)"
        >
          <v-icon small class="mr-1">mdi-palette</v-icon>
          LiveEditor
        </v-btn>
        <v-btn
          v-else
          small
          depressed
          color="warning"
          :disabled="uploaded"
          @click="openReUploadDialog"
        >
          <v-icon small class="mr-1">mdi-upload</v-icon>
          Neues Ergebnis
        </v-btn>
      </template>
      <v-btn
        v-else
        width="100%"
        :disabled="rating.shared || isOwnFeedback"
        @click="
          () => {
            showShareDialog = true;
          }
        "
        small
        color="success"
        >{{ ratingBtnText }}</v-btn
      >
    </v-card-actions>
    <!-- Bestätigung: Feedback auflösen ohne Änderung -->
    <v-dialog v-model="showResolveDialog" max-width="440">
      <v-card>
        <v-card-title>Feedback auflösen?</v-card-title>
        <v-card-text>
          Das Feedback wird gelöscht und die Bestellung geht mit dem
          <strong>aktuellen Ergebnis</strong> in Produktion.
          Der Kunde erhält keine weitere Nachricht.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="resolving" @click="showResolveDialog = false">Abbrechen</v-btn>
          <v-btn color="success" depressed :loading="resolving" @click="resolveWithoutChanges">
            <v-icon small class="mr-1">mdi-thumb-up</v-icon>
            In Produktion geben
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <PushTaskBackDialog
      v-if="showPushTaskBackDialog"
      :status="['DRAW', 'TEMPLATING']"
      :show="showPushTaskBackDialog"
      :task-uid="rating.taskUid"
      :on-close="closePushBackDialog"
      :on-push-back="markAsPushedBack"
      :set-resolved="setAsResolved"
    />
    <ReUploadDialog
      v-if="showReUploadDialog"
      :show="showReUploadDialog"
      :task="rating.task"
      :on-close="() => (showReUploadDialog = false)"
      :set-resolved="setAsResolved"
    />
    <ReassignDialog
      v-if="showReassignDialog"
      :show="showReassignDialog"
      :task="rating.task"
      :on-close="closeReassignDialog"
      :persistent="false"
      :set-resolved="setAsResolved"
    />
    <OrderMailDialog
      v2
      v-if="showMailDialog"
      :show="showMailDialog"
      :on-close="closeMailDialog"
      :orderId="rating.orderId"
      mail-category="RATING"
      :wait-for-sent-success="false"
    />
    <ShareRatingDialog
      @shared="onShared"
      :close="
        () => {
          showShareDialog = false;
        }
      "
      :show="showShareDialog"
      :rating="rating"
    />
    <LiveEditorDialog
        v-if="task.customizationData"
      v-show="showLiveEditor"
      v-model="showLiveEditor"
      :task="task"
      :customizationData="task.customizationData"
      :orderName="task.orderName"
      :upscaledUrls="{ DEFAULT: task.customizationData?.upscaledImage }"
      @save="handleLiveEditorSave"
      @approveProcessed="showLiveEditor = false"
      @approve="({ finalUrl, sendPreviewCustomer, refinementUrl, currentConfig }) => pushToFulFill(finalUrl, sendPreviewCustomer, refinementUrl, currentConfig)"
      @refinement="({ currentConfig, customFinalImage, revisionComment }) => pushToRefinement(revisionComment, customFinalImage, currentConfig)"

    />
  </v-card>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ShareRatingDialog from "@/components/AllRatings/ShareRatingDialog";
import { getDateFromNow, openOrderModal, decodeHtmlEntities } from "@/utils";
import PushTaskBackDialog from "@/components/PushTaskBackDialog";
import OrderMailDialog from "@/components/OrderMailDialog";
import { mailTemplateTypes } from "@/content/mails";
import ReassignDialog from "@/components/AllRatings/ReassignDialog";
import OrderNoteTooltip from "@/components/OrderNoteTooltip";
import ReUploadDialog from "@/components/AllRatings/ReUploadDialog.vue";
import FeedbackThread from "@/components/AllRatings/FeedbackThread.vue";
import LiveEditorDialog from "@/components/FulfillOrders/LiveEditorDialog.vue";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { EventBus, events } from "../../../event-bus";
import {auth, db, functions} from "../../../fb";
import { taskStatus } from "@/constants";
import {httpsCallable} from "firebase/functions";
import SupportDialog from "@/components/Support/SupportTooltip.vue";

export default {
  name: "RatingCardAdmin",
  components: {
    SupportDialog,
    LiveEditorDialog,
    FeedbackThread,
    ReUploadDialog,
    OrderNoteTooltip,
    ReassignDialog,
    OrderMailDialog,
    PushTaskBackDialog,
    ShareRatingDialog,
  },
  data() {
    return {
      editLinkBusy: false,
      showLiveEditor: false,
      artistName: "",
      resolved: false,
      expandComment: false,
      showShareDialog: false,
      showPushTaskBackDialog: false,
      showMailDialog: false,
      pushedBack: false,
      uploaded: false,
      showReassignDialog: false,
      showOrderNoteDialog: false,
      showReUploadDialog: false,
      showResolveDialog: false,
      resolving: false,
      // Countdown-Tick für den Auto-Fix-Status
      now: Date.now(),
      nowInterval: null,
    };
  },
  props: {
    rating: {
      required: true,
      type: Object,
    },
  },
  computed: {
    ...mapGetters(["userNameIdMap", "currentUser"]),
    mailTemplateType() {
      return mailTemplateTypes.REVISE;
    },
    ratingBtnText() {
      return this.rating.shared ? `Shared` : `Share`;
    },
    isOwnFeedback() {
      return this.rating.artistId === this.currentUser.uid;
    },
    timeAgo() {
      return getDateFromNow(this.rating.timestamp);
    },
    task() {
      return this.rating.task;
    },
    /** Zugeklappte Vorschau: neuester Kommentar — deutsche Kommentare im
     *  Original, sonst Übersetzung; HTML-Entities dekodiert */
    latestCommentText() {
      const comments = this.task?.feedback?.comments;
      if (Array.isArray(comments) && comments.length) {
        const latest = comments[comments.length - 1];
        const isGerman =
          String(latest.locale || "").toLowerCase().startsWith("de") ||
          /^\(DE\)/.test(latest.translated || "");
        const text = isGerman
          ? latest.original || latest.translated
          : latest.translated || latest.original;
        return decodeHtmlEntities(text || "");
      }
      return decodeHtmlEntities(this.rating.comment);
    },
    autoEdit() {
      return this.rating.autoEdit || this.task?.feedback?.autoEdit || null;
    },
    /** Läuft gerade ein KI-Edit? Auto-Fix (feedback.autoEdit) oder ein
     *  manuell im LiveEditor gestarteter Edit (customizationData.aiEditPending) */
    aiEditRunning() {
      if (this.rating.satisfied) return false;
      // scheduled zählt mit: aus Nutzersicht "kümmert sich die KI" ab dem
      // Moment, in dem der Fix eingeplant ist (Countdown-Chip)
      return (
        this.autoEdit?.status === "scheduled" ||
        this.autoEdit?.status === "running" ||
        !!this.task?.customizationData?.aiEditPending
      );
    },
    /** Anzeige-Logik des Auto-KI-Fix als ein Chip-Descriptor */
    autoEditChip() {
      const ae = this.autoEdit;
      if (!ae || this.rating.satisfied) return null;

      if (ae.status === "scheduled") {
        const msLeft = Math.max(0, (ae.runAfter || 0) - this.now);
        const m = Math.floor(msLeft / 60000);
        const s = Math.floor((msLeft % 60000) / 1000);
        return {
          tone: "waiting",
          icon: "mdi-clock-outline",
          label: msLeft > 0 ? `KI-Fix in ${m}:${String(s).padStart(2, "0")}` : "KI-Fix startet gleich…",
          canRunNow: true,
          canCancel: true,
        };
      }
      if (ae.status === "running") {
        return { tone: "running", spinner: true, label: ae.labJobId ? "Feedback-Edit läuft (Kritik wird eingeordnet)…" : "KI-Fix läuft…" };
      }
      if (ae.status === "done") {
        const parts = [];
        // Feedback-Edit der Upscaler-Kette: kleinster Eingriff statt Voll-Generierung
        if (ae.scope && ae.scopeLabel) parts.push(ae.scopeLabel);
        else if (ae.editName) parts.push(`${ae.editName} erstellt`);
        if (ae.configProposal) parts.push("Text-Vorschlag bereit");
        else if (ae.textOverlay) parts.push(`Text manuell: ${ae.textRequest || "siehe Kommentar"}`);
        if (!parts.length) parts.push("KI-Fix fertig");
        return {
          tone: ae.textOverlay && !ae.configProposal && !ae.editName ? "manual" : "done",
          icon: "mdi-creation",
          label: parts.join(" · "),
          opensEditor: true,
        };
      }
      if (ae.status === "noAction") {
        // Kritik nicht zuordenbar → bewusst nichts generiert, Mensch schaut drauf
        if (ae.scope === "unclear") return { tone: "manual", icon: "mdi-account-search-outline", label: `Manuell prüfen${ae.reason ? `: ${ae.reason}` : ""}`, opensEditor: true };
        return { tone: "muted", icon: "mdi-information-outline", label: "KI: keine Aktion nötig" };
      }
      if (ae.status === "limit") {
        return {
          tone: "error",
          icon: "mdi-gauge-full",
          label: "Tageslimit erreicht",
          canRunNow: true,
        };
      }
      if (ae.status === "failed") {
        return {
          tone: "error",
          icon: "mdi-alert-circle-outline",
          label: `KI-Fix fehlgeschlagen${ae.error ? `: ${ae.error}` : ""}`,
          canRunNow: true,
        };
      }
      if (ae.status === "cancelled") {
        return {
          tone: "muted",
          icon: "mdi-cancel",
          label: "Auto-Fix abgebrochen",
          canRunNow: true,
        };
      }
      return null;
    },
  },
  created() {
    const user = this.userNameIdMap.find(
      (user) => user.uid === this.rating.artistId
    );
    this.artistName = user?.username || "unknown";
    this.nowInterval = setInterval(() => (this.now = Date.now()), 5000);
  },
  beforeDestroy() {
    clearInterval(this.nowInterval);
  },
  methods: {
    /** Änderungslink per Mail (orderEditLink): parkt den Task sofort, der Platzhalter wartet in der Import-Liste */
    async sendEditLink() {
      if (!window.confirm("Änderungslink per Mail senden? Der Task wird sofort geparkt, die Bewertung verschwindet aus der Liste, die Bestellung wartet auf die Änderung des Kunden.")) return;
      this.editLinkBusy = true;
      try {
        const fn = httpsCallable(functions, "orderEditLink", { timeout: 60000 });
        const { data } = await fn({ action: "mail", orderUid: String(this.task.orderId || ""), taskUid: String(this.task.uid) });
        EventBus.$emit(events.SNACKBAR, { message: `Mail an ${data.mailTo} gesendet, Link gültig bis ${new Date(data.expiresAt).toLocaleString("de-DE")}`, type: "success" });
      } catch (e) {
        console.error("Änderungslink:", e);
        EventBus.$emit(events.SNACKBAR, { message: `Änderungslink: ${e.message || e}`, type: "error" });
      } finally {
        this.editLinkBusy = false;
      }
    },
    ...mapActions(["sendResultsToCustomer", "updateLiveTask"]),
    handleLiveEditorSave() {},
    /** feedback.autoEdit in beiden Task-Docs patchen (tasks/ + shopifyTasks/) */
    async patchAutoEdit(patch) {
      const uid = String(this.task.uid);
      await Promise.all(
        ["tasks", "shopifyTasks"].map(col =>
          updateDoc(doc(db, col, uid), patch).catch(() => {})
        )
      );
    },
    runAutoEditNow() {
      // force umgeht Wartezeit UND Tageslimit — bewusste manuelle Entscheidung
      this.patchAutoEdit({
        "feedback.autoEdit.status": "scheduled",
        "feedback.autoEdit.force": true,
        "feedback.autoEdit.runAfter": Date.now(),
      });
    },
    cancelAutoEdit() {
      this.patchAutoEdit({ "feedback.autoEdit.status": "cancelled" });
    },
    pushToFulFill( url = null, sendPreviewCustomer = false, refinementUrl, currentConfig) {
      this.updateLiveTask({
        task: {
          ...this.task,
        },
        finalArtworkUrl: url,
        refinementUrl,
        currentConfig,
        callBack: this.setAsResolved,
        sendPreviewCustomer: sendPreviewCustomer,
        resolveFeedback: true,
      });
    },
    pushToRefinement(revisionComment, refinementUrl, currentConfig) {
      this.updateLiveTask({
        task: {
          ...this.task,
        },
        refinementUrl,
        revisionComment,
        currentConfig,
        status: taskStatus.REFINEMENT,
        callBack: this.setAsResolved,
        sendPreviewCustomer: false,
        // Überarbeitung nach 👎 = nächste Feedbackrunde beginnt — das alte
        // Feedback ist damit erledigt (der Anlass steckt im revisionComment)
        resolveFeedback: true,
      });
    },
    getImageDimensions(imageSrc) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = function () {
          const orientation =
              this.width > this.height ? "landscape" : "portrait";
          resolve(orientation);
        };
        img.onerror = reject;
        img.src = imageSrc;
      });
    },
    async uploadAndSendResults(url) {
      this.isSaving = true;
      console.log(this.task.uid);
      await setDoc(
        doc(db, `tasks`, this.task.uid),
        {
          finalTemplate: url,
          finalTemplateThumbnailUrl: url,
          isRevision: "",
          feedback: null,
          status: taskStatus.CLOSED,
        },
        {
          merge: true,
        }
      );

      const orientation = await this.getImageDimensions(url);

      await this.sendResultsToCustomer({
        taskUid: this.task.uid,
        template: url,
        orientation: orientation,
        isRevision: true,
      });
      this.resolved = true;
      this.showPushTaskBackDialog = false;
      this.showLiveEditor = false;
    },
    setAsResolved() {
      this.resolved = true;
    },
    /** Daumen hoch: Feedback löschen, Task mit dem AKTUELLEN Ergebnis in
     *  Produktion geben — gleicher Weg wie der LiveEditor-Approve
     *  (updateLiveTask, Status CLOSED), nur ohne neues Artwork und ohne Mail. */
    async resolveWithoutChanges() {
      this.resolving = true;
      try {
        await this.updateLiveTask({
          task: { ...this.task },
          callBack: this.setAsResolved,
          resolveFeedback: true,
        });
        this.showResolveDialog = false;
      } finally {
        this.resolving = false;
      }
    },
    openOrder(uid) {
      openOrderModal(uid);
    },
    openReUploadDialog() {
      this.showReUploadDialog = true;
    },
    onShared() {
      this.$emit("feedbackShared", this.rating.taskUid);
    },
    openPushBackDialog() {
      this.showPushTaskBackDialog = true;
    },
    closePushBackDialog() {
      this.showPushTaskBackDialog = false;
    },
    openMailDialog() {
      this.showMailDialog = true;
    },
    closeMailDialog() {
      this.showMailDialog = false;
    },
    markAsPushedBack() {
      this.pushedBack = true;
    },
    openReassignDialog() {
      this.showReassignDialog = true;
    },
    closeReassignDialog() {
      this.showReassignDialog = false;
    },
    searchOrderInMails() {
      const terms = [
        this.task.orderName?.split("#")[1],
        this.task.customerEmail,          // Bestell-Email des Kunden
      ].filter(Boolean);

      window.open(
          `https://quelp.ai/inbox?q=${encodeURIComponent(terms.join(","))}`,
          "_blank"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.rc-card {
  border-radius: 14px !important;
}

/* Umlaufender Regenbogen-Glow (Gemini-Stil), solange ein KI-Edit läuft.
   Technik: rotierender conic-gradient als Donut (padding + mask-composite
   stanzt die Kartenfläche aus) — malt ÜBER den Container-Hintergründen.
   (z-index:-1 wäre hinter den opaken v-main-/Panel-Hintergründen unsichtbar.)
   ::after = scharfer Farbring, ::before = weicher Schein (blur).
   Die Winkel-Animation braucht @property (Chrome/Edge); ohne Support
   bleibt ein statischer Farbring — ebenfalls klar erkennbar. */
.rc-card--ai-running {
  position: relative;
  overflow: visible !important;

  &::before,
  &::after {
    content: "";
    position: absolute;
    background: conic-gradient(
      from var(--rc-glow-angle, 0deg),
      #4285f4, #9b72cb, #d96570, #f2a60c, #34a853, #4285f4
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: rc-glow-spin 3.5s linear infinite;
    pointer-events: none;
  }

  &::after {
    inset: -2px;
    border-radius: 16px;
    padding: 2px;
  }

  &::before {
    inset: -6px;
    border-radius: 20px;
    padding: 6px;
    filter: blur(10px);
    opacity: 0.45;
  }
}

@property --rc-glow-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}

@keyframes rc-glow-spin {
  to {
    --rc-glow-angle: 360deg;
  }
}

.rc-live-badge {
  margin-left: 10px;
  background: #f4f0fd;
  color: #7b5cd6;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 999px;
  padding: 2px 9px;
}

.card-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 3;
  background: rgba(115, 115, 115, 0.3);
}

.rating-actions {
  position: relative;
  z-index: 4 !important;
}

.card-content {
  height: 380px;
  display: flex;
  flex-direction: column;
}

/* Mockup füllt den verfügbaren Platz oberhalb der Sprechblase */
.rc-mockup {
  flex: 1 1 auto;
  min-height: 0;
}

/* Kommentar-Vorschau: Sprechblase, 2 Zeilen, Klick öffnet den Thread */
.rc-comment {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  background: #f6f8fa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 9px 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #eef0f3;
  }

  &__icon {
    color: rgba(0, 0, 0, 0.3) !important;
    align-self: flex-start;
    margin-top: 1px;
  }

  &__text {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.75);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__chevron {
    color: rgba(0, 0, 0, 0.35) !important;
  }
}

/* Aufgeklappter Thread: kompaktes Bild + eine Kommentar-Box im gleichen Stil
   wie die eingeklappte Sprechblase, füllt die restliche Kartenhöhe */
.rc-thread {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  cursor: pointer;

  &__collapse {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    color: rgba(0, 0, 0, 0.35) !important;
  }

  &__img {
    flex-shrink: 0;
    margin-bottom: 8px;
  }

  /* Die Box selbst (wie .rc-comment): einheitlicher Rahmen, schwarzer Text,
     schlanke integrierte Scrollbar. Die einzelnen Kommentar-Kästchen aus
     FeedbackThread werden flach gemacht — der äußere Kasten IST die Box. */
  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: #f6f8fa;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    padding: 10px 12px;

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.18);
      border-radius: 3px;
    }
    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.3);
    }

    ::v-deep .fbt__entry {
      background: transparent;
      border: 0;
      border-radius: 0;
      padding: 0;
      margin: 0;
    }
    ::v-deep .fbt__entry:not(:first-child) {
      opacity: 1;
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid rgba(0, 0, 0, 0.07);
    }
    ::v-deep .fbt__text {
      color: rgba(0, 0, 0, 0.87);
      font-size: 13.5px;
    }
    ::v-deep .fbt__toggle-row {
      margin-bottom: 8px;
    }
  }
}

/* Auto-KI-Fix-Status: Chip links in der Aktionszeile; darf schrumpfen
   und schneidet dann mit Ellipsis ab, statt die Zeile umzubrechen */
.rc-autoedit {
  &__chip {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    border-radius: 999px;
    padding: 3px 11px;
    min-width: 0;
    flex-shrink: 1;
    margin-right: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 20px;

    .v-progress-circular {
      vertical-align: middle;
    }

    .v-icon {
      color: inherit !important;
    }

    &--waiting {
      background: #fdf4e3;
      color: #a96617;
    }

    &--running {
      background: #e8f0fd;
      color: #2f5fb3;
    }

    &--done {
      background: #f4f0fd;
      color: #7b5cd6;
    }

    &--manual {
      background: #fdeeda;
      color: #a96617;
    }

    &--muted {
      background: #eef0f3;
      color: rgba(0, 0, 0, 0.5);
    }

    &--error {
      background: #fdeaea;
      color: #b71c1c;
    }
  }
}

</style>
